import type { FC, ReactElement } from 'react';

import classNames from 'classnames';

import { formatLocalDate } from '@iconik-io/core/src/date/format';
import type {
  SearchCriteriaFilterSchema,
  SearchCriteriaTerm,
  SearchCriteriaTermSchema,
  SearchSearchCriteriaSchema,
  SearchSearchHistorySchema,
} from '@iconik-io/iconik-api/src/services/search';

import Ellipsis from '../../components/ellipsis/ellipsis';
import { Link } from '../../components/link/link';
import Label from '../../ui/label/label';

import mixins from '../../styles/core/mixins.module.css';
import styles from './search_history.module.css';

function properCase(name: string) {
  // capitalize first letter of each word
  return name.toLowerCase().replace(/^\w|\s\w/g, (str) => str.toUpperCase());
}

type FilterTerm = ReactElement | string;
const convertFilter = (
  item: SearchCriteriaTerm | SearchCriteriaTermSchema,
): FilterTerm[] => {
  if (item.name === 'status' || item.name === 'id') {
    return [];
  }
  if (item.name === 'in_collections') {
    return [
      <Link
        key="in_collections"
        className={styles.link}
        to={`/collection/${item.value ?? item.value_in?.[0]}`}
      >
        COLLECTION
      </Link>,
    ];
  }
  if (item.value_in) {
    const valueIn =
      item.name === 'is_online'
        ? Array.from(item.value_in, (value) => Boolean(Number(value)))
        : item.value_in;
    return [
      // remove underscore from item name
      `${properCase(item.name.replace(/_+/g, ' ').trim())}: ${valueIn
        .toString()
        .split(',')
        .join(', ')}`,
    ];
  }
  if (item.value) {
    return [item.value];
  }
  if (item.range) {
    return [`${item.range.min}..${item.range.max}`];
  }
  return [];
};

type FilterTermWithColor = { value: FilterTerm; color: string };
const convertFilters = (
  doc: SearchCriteriaFilterSchema,
): FilterTermWithColor[] => {
  let terms: FilterTermWithColor[] = [];
  doc.terms
    ?.filter((term) => term.name !== 'is_root')
    .forEach((x) => {
      terms = terms.concat(
        convertFilter(x).map((value) => ({ value, color: styles.filter })),
      );
    });
  if (doc.filters) {
    doc.filters.forEach((f) => {
      terms = terms.concat(convertFilters(f));
    });
  }
  return terms;
};

const convertCriteria = (
  data: SearchSearchCriteriaSchema,
): FilterTermWithColor[] => {
  let filters: FilterTermWithColor[] = [];
  if (data.query) {
    filters.push({ value: data.query, color: styles.query });
  }
  const facetsFilters = data.facets_filters || [];
  facetsFilters.forEach((item) => {
    filters = filters.concat(
      convertFilter(item).map((value) => ({
        value,
        color: styles.facet_filter,
      })),
    );
  });
  if (data.filter) {
    filters = filters.concat(convertFilters(data.filter || {}));
  }
  return filters;
};

type SearchHistoryItemProps = {
  criteria: SearchSearchCriteriaSchema;
};

const SearchHistoryItem: FC<SearchHistoryItemProps> = ({ criteria }) => {
  const tags = convertCriteria(criteria).filter((n) => n);
  return (
    <div className={styles.tags} aria-label="Search Criteria" role="list">
      {tags.map((item, i) => (
        <div
          role="listitem"
          aria-label={item.value.toString()}
          className={styles.tag}
          key={i}
        >
          <Label className={item.color}>
            <Ellipsis
              componentProps={{ className: styles.wrap_text }}
              tooltipText={item.value.toString()}
              component="span"
            >
              {item.value}
            </Ellipsis>
          </Label>
        </div>
      ))}
    </div>
  );
};

type Props = {
  searchHistory: SearchSearchHistorySchema[];
  onSelect: (criteria: SearchSearchCriteriaSchema) => void;
  dateFormat: string;
};

const SearchHistoryPanel: FC<Props> = (props) => {
  const { searchHistory, onSelect, dateFormat } = props;

  return (
    <div role="list" aria-label="Search History List">
      {searchHistory.map((item) => (
        <div className={styles.item} role="listitem" key={item.id}>
          <button
            className={classNames(styles.button, mixins.reset_button)}
            onClick={() => onSelect(item.criteria)}
          >
            <div className={styles.date}>
              {formatLocalDate(item.date_created, dateFormat)}
            </div>
            <SearchHistoryItem criteria={item.criteria} />
          </button>
        </div>
      ))}
    </div>
  );
};

export default SearchHistoryPanel;
